import React from 'react';
import { PAGINATION_OPTIONS } from '../data/constants';

const PaginationControls = ({ pagination, onPageChange, onRowsPerPageChange }) => {
  if (!pagination || pagination.totalCount === undefined || pagination.totalCount <= 0) {
    return null;
  }

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    
    // Calculate the first item of the current page
    const currentFirstItem = (pagination.currentPage - 1) * pagination.pageSize + 1;
    
    // Calculate which page this item would be on with the new page size
    const newPage = Math.ceil(currentFirstItem / newRowsPerPage);
    
    // Call onPageChange with the new page number and page size
    if (onPageChange) {
      onPageChange(newPage, newRowsPerPage);
    }
    
    if (onRowsPerPageChange) {
      onRowsPerPageChange(event);
    }
  };

  return (
    <div className="bg-[#f0fff3] px-4 py-3 border-t border-[#d7f5df] flex items-center justify-between">
      <div className="flex-1 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{((pagination.currentPage - 1) * pagination.pageSize) + 1}</span> to{' '}
            <span className="font-medium">
              {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalCount)}
            </span>{' '}
            of <span className="font-medium">{pagination.totalCount}</span> results
          </p>
        </div>
        <div className="flex items-center">
          <div className="mr-4">
            <label htmlFor="rowsPerPage" className="sr-only">Rows per page</label>
            <select
              id="rowsPerPage"
              value={pagination.pageSize}
              onChange={handleRowsPerPageChange}
              className="block w-full pl-3 pr-10 py-1 text-base border-gray-300 focus:outline-none focus:ring-[#96ffa8] focus:border-[#96ffa8] sm:text-sm rounded-md"
            >
              {PAGINATION_OPTIONS.map(option => (
                <option key={option} value={option}>{option} per page</option>
              ))}
            </select>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => onPageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className={`relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium ${
                  pagination.currentPage === 1
                    ? 'border-gray-300 bg-white text-gray-300 cursor-not-allowed'
                    : 'border-gray-300 bg-white text-gray-500 hover:bg-[#f0fff3]'
                }`}
              >
                <span className="sr-only">Previous</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              
              {/* Page numbers */}
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                .filter(page => {
                  // Show current page, first and last pages, and pages around current page
                  return (
                    page === 1 || 
                    page === pagination.totalPages || 
                    Math.abs(page - pagination.currentPage) <= 1
                  );
                })
                .map((page, index, array) => {
                  // Add ellipsis
                  const showEllipsisBefore = index > 0 && array[index - 1] !== page - 1;
                  const showEllipsisAfter = index < array.length - 1 && array[index + 1] !== page + 1;
                  
                  return (
                    <React.Fragment key={page}>
                      {showEllipsisBefore && (
                        <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      )}
                      
                      <button
                        onClick={() => onPageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === pagination.currentPage
                            ? 'z-10 bg-[#96ffa8] border-[#96ffa8] text-black font-medium'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-[#f0fff3]'
                        }`}
                      >
                        {page}
                      </button>
                      
                      {showEllipsisAfter && (
                        <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      )}
                    </React.Fragment>
                  );
                })}
              
              <button
                onClick={() => onPageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className={`relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium ${
                  pagination.currentPage === pagination.totalPages
                    ? 'border-gray-300 bg-white text-gray-300 cursor-not-allowed'
                    : 'border-gray-300 bg-white text-gray-500 hover:bg-[#f0fff3]'
                }`}
              >
                <span className="sr-only">Next</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaginationControls;